import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../../lib/http";
import {
  getWorkspaceBilling,
  getWorkspaceClients,
} from "../services/usersService";
import type {
  Invoice,
  UserClient,
  UserDetail,
  UserDetailTabId,
} from "../types/users.types";

/** How many rows each of the two tables holds. Sent as `page_size`. */
export const DETAIL_ROWS_PER_PAGE = 10;

/**
 * Where the detail screen is.
 *
 * `notFound` is its own state rather than a flavour of `failed`, because the
 * two want opposite answers: a failed fetch is worth retrying and a 404 never
 * will be, and telling somebody a record is gone when the server was merely
 * unreachable sends them looking for something that is sitting there.
 */
export type DetailStatus = "loading" | "ready" | "notFound" | "failed";

/**
 * One account, and one page of whichever of its two lists is on screen.
 *
 * This used to be three calls fired together — the account, its whole client
 * roster, its whole billing history — and it is one now, because the endpoint
 * is one: both views answer with the account under `practitioner`, so the tab
 * that is open is what decides the request, and the header fills in either
 * way.
 *
 * Two consequences worth naming:
 *
 * - **Switching tabs fetches.** Cheap, and it is what keeps the two lists
 *   paged on the server rather than held whole in the browser. The rows and
 *   the account already in hand stay on screen while it happens, so a tab
 *   change is not a blank screen; only the first load of all has nothing to
 *   show.
 *
 * - **There is no longer a half-loaded screen.** The account and its rows
 *   arrive together or not at all, so a table can no longer fail on its own
 *   while the details above it read fine. One failure, one answer to it.
 *
 * A late answer to a superseded request is dropped — switching tabs twice
 * quickly, or paging through, leaves two requests out with no guarantee of
 * the order they return in.
 *
 * `total` is null until the open view has been counted, rather than 0: the
 * headings print it, and a zero standing in for "not yet known" is a figure
 * that reads as an answer.
 */
export function useUserDetail(
  userId: number | null,
  view: UserDetailTabId,
  page: number
) {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [clients, setClients] = useState<UserClient[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  // Tagged with the view it counts. Switching tabs would otherwise leave the
  // new tab's heading wearing the old one's figure until the answer landed —
  // "Billing history (24)" over a practice's client count.
  const [counted, setCounted] = useState<{
    view: UserDetailTabId;
    total: number;
  } | null>(null);
  const [status, setStatus] = useState<DetailStatus>("loading");

  const requestId = useRef(0);

  const load = useCallback(async () => {
    // A token that did not decode arrives here as null. There is nothing to
    // ask the server, and the answer would be a 404 anyway.
    if (userId === null) {
      setStatus("notFound");
      return;
    }

    const id = ++requestId.current;
    setStatus("loading");

    try {
      if (view === "billing") {
        const result = await getWorkspaceBilling(
          userId,
          page,
          DETAIL_ROWS_PER_PAGE
        );
        if (id !== requestId.current) return;
        setInvoices(result.rows);
        setUser(result.user);
        setCounted({ view, total: result.total });
      } else {
        const result = await getWorkspaceClients(
          userId,
          page,
          DETAIL_ROWS_PER_PAGE
        );
        if (id !== requestId.current) return;
        setClients(result.rows);
        setUser(result.user);
        setCounted({ view, total: result.total });
      }

      setStatus("ready");
    } catch (error) {
      if (id !== requestId.current) return;
      setStatus(
        error instanceof ApiError && error.status === 404 ? "notFound" : "failed"
      );
    }
  }, [userId, view, page]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    user,
    clients,
    invoices,
    /** How many rows the open view has, or null before its answer has landed. */
    total: counted?.view === view ? counted.total : null,
    status,
    reload: load,
  };
}

export default useUserDetail;
